import axios, { AxiosResponse } from 'axios';
import { AuthResponse, User, Aluno, Empresa, Estagio, Cidade, Curso, Instituicao, Seguradora, EstagioHorario, RelatorioEstagio, EstagioFilters, EmpresaFilters, PaginationParams } from '@/types';
import { fetchAll, fetchById, create, update, deleteById, fetchBy } from './apiHelpers';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://p0oswcss8sggs44c8g0gsw00.72.60.250.39.sslip.io',
  timeout: 30000, // 30 segundos de timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token automaticamente
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor para tratar erros de resposta
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('❌ Erro na API:', {
      status: error.response?.status,
      message: error.message,
      code: error.code,
      url: error.config?.url
    });

    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    } else if (error.response?.status === 502) {
      console.error('Bad Gateway - Servidor backend não está respondendo');
      // Toast já é tratado pelo useErrorHandler, não precisa de window.confirm
    } else if (error.code === 'ECONNABORTED') {
      console.error('Timeout - Servidor demorou para responder');
    } else if (error.code === 'ERR_NETWORK') {
      console.error('Erro de rede - Verifique sua conexão');
    }

    return Promise.reject(error);
  }
);

// Serviços de Autenticação
export const authService = {
  login: async (email: string, senha: string): Promise<AuthResponse> => {
    const response: AxiosResponse<AuthResponse> = await api.post('/usuario/login', {
      email,
      senha,
    });
    return response.data;
  },

  register: async (nome: string, email: string, senha: string): Promise<User> => {
    const response: AxiosResponse<User> = await api.post('/usuario', {
      nome,
      email,
      senha,
    });
    return response.data;
  },

  changePassword: async (id: number, senhaAtual: string, novaSenha: string): Promise<void> => {
    await api.put(`/usuario/senha/${id}`, {
      senhaAtual,
      novaSenha,
    });
  },

  getProfile: async (id: number): Promise<User> => {
    const response: AxiosResponse<User> = await api.get(`/usuario/${id}`);
    return response.data;
  },
};

// Serviços de Alunos
export const alunoService = {
  list: (filters?: PaginationParams) => fetchAll<Aluno>('/aluno', filters),
  getById: (id: number) => fetchById<Aluno>('/aluno', id),
  create: (data: Omit<Aluno, 'id' | 'cidade' | 'curso'>) => create<Aluno>('/aluno', data),
  update: (id: number, data: Partial<Omit<Aluno, 'id' | 'cidade' | 'curso'>>) => update<Aluno>('/aluno', id, data),
  delete: (id: number) => deleteById('/aluno', id),
};

// Serviços de Empresas
export const empresaService = {
  list: (filters?: EmpresaFilters) => fetchAll<Empresa>('/empresa', filters),
  getById: (id: number) => fetchById<Empresa>('/empresa', id),
  create: (data: Omit<Empresa, 'id'>) => create<Empresa>('/empresa', data),
  update: (id: number, data: Partial<Empresa>) => update<Empresa>('/empresa', id, data),
  delete: (id: number) => deleteById('/empresa', id),
};

// Serviços de Estágios
export const estagioService = {
  list: (filters?: EstagioFilters) => fetchAll<Estagio>('/estagio', filters),
  getById: (id: number) => fetchById<Estagio>('/estagio', id),
  getByStatus: (status: string) => fetchBy<Estagio>('/estagio', 'status', status),
  create: (data: Omit<Estagio, 'id'>) => create<Estagio>('/estagio', data),
  update: (id: number, data: Partial<Estagio>) => update<Estagio>('/estagio', id, data),
  delete: (id: number) => deleteById('/estagio', id),
  generateTerm: async (id: number): Promise<Blob> => {
    const response = await api.post(`/gerar-termo/${id}/template`, {}, {
      responseType: 'blob',
    });
    return response.data;
  },
};

// Serviços de Cidades
export const cidadeService = {
  list: (filters?: PaginationParams) => fetchAll<Cidade>('/cidade', filters),
  getById: (id: number) => fetchById<Cidade>('/cidade', id),
  getByName: (nome: string) => fetchBy<Cidade>('/cidade', 'nome', nome),
  getByUf: (uf: string) => fetchBy<Cidade>('/cidade', 'uf', uf),
  create: (data: Omit<Cidade, 'id'>) => create<Cidade>('/cidade', data),
  update: (id: number, data: Partial<Cidade>) => update<Cidade>('/cidade', id, data),
  delete: (id: number) => deleteById('/cidade', id),
};

// Serviços de Cursos
export const cursoService = {
  list: (filters?: PaginationParams) => fetchAll<Curso>('/curso', filters),
  getById: (id: number) => fetchById<Curso>('/curso', id),
  create: (data: Omit<Curso, 'id'>) => create<Curso>('/curso', data),
  update: (id: number, data: Partial<Curso>) => update<Curso>('/curso', id, data),
  delete: (id: number) => deleteById('/curso', id),
};

// Serviços de Instituições
export const instituicaoService = {
  list: (filters?: PaginationParams) => fetchAll<Instituicao>('/instituicao', filters),
  getById: (id: number) => fetchById<Instituicao>('/instituicao', id),
  create: (data: Omit<Instituicao, 'id'>) => create<Instituicao>('/instituicao', data),
  update: (id: number, data: Partial<Instituicao>) => update<Instituicao>('/instituicao', id, data),
  delete: (id: number) => deleteById('/instituicao', id),
};

// Serviços de Seguradoras
export const seguradoraService = {
  list: (filters?: PaginationParams) => fetchAll<Seguradora>('/seguradora', filters),
  getById: (id: number) => fetchById<Seguradora>('/seguradora', id),
  create: (data: Omit<Seguradora, 'id'>) => create<Seguradora>('/seguradora', data),
  update: (id: number, data: Partial<Seguradora>) => update<Seguradora>('/seguradora', id, data),
  delete: (id: number) => deleteById('/seguradora', id),
};

// Serviços de Horários de Estágio
export const estagioHorarioService = {
  list: (filters?: PaginationParams) => fetchAll<EstagioHorario>('/estagio-horario', filters),
  getById: (id: number) => fetchById<EstagioHorario>('/estagio-horario', id),
  getByEstagio: (estagioId: number) => fetchBy<EstagioHorario>('/estagio-horario', 'estagio', estagioId),
  create: (data: Omit<EstagioHorario, 'id' | 'createdAt' | 'updatedAt'>) => create<EstagioHorario>('/estagio-horario', data),
  createLote: async (data: {
    estagioId: number;
    diasSemana: string[];
    horarios: { horarioInicio: string; horarioFim: string; }[];
  }): Promise<EstagioHorario[]> => {
    const response: AxiosResponse<EstagioHorario[]> = await api.post('/estagio-horario/lote', data);
    return response.data;
  },
  update: (id: number, data: Partial<EstagioHorario>) => update<EstagioHorario>('/estagio-horario', id, data),
  delete: (id: number) => deleteById('/estagio-horario', id),
  deleteByEstagio: async (estagioId: number): Promise<{ count: number; mensagem: string }> => {
    const response: AxiosResponse<{ count: number; mensagem: string }> = await api.delete(`/estagio-horario/estagio/${estagioId}`);
    return response.data;
  },
};

// Serviços de Relatórios de Estágio
export const relatorioEstagioService = {
  list: (filters?: PaginationParams) => fetchAll<RelatorioEstagio>('/relatorio-estagio', filters),
  getById: (id: number) => fetchById<RelatorioEstagio>('/relatorio-estagio', id),
  getByEstagio: (estagioId: number) => fetchBy<RelatorioEstagio>('/relatorio-estagio', 'estagio', estagioId),
  create: (data: Omit<RelatorioEstagio, 'id' | 'createdAt' | 'updatedAt'>) => create<RelatorioEstagio>('/relatorio-estagio', data),
  update: (id: number, data: Partial<RelatorioEstagio>) => update<RelatorioEstagio>('/relatorio-estagio', id, data),
  delete: (id: number) => deleteById('/relatorio-estagio', id),
};


// Serviços de Geração de Termos
export const gerarTermoService = {
  listEstagios: (filters?: PaginationParams) => fetchAll<Estagio>('/estagio', filters),
  gerarTermo: async (estagioId: number): Promise<Blob> => {
    const response: AxiosResponse<Blob> = await api.get(`/gerar-termo/${estagioId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

// Serviços de Estatísticas
export const statsService = {
  getDashboardStats: async () => {
    try {
      const [alunos, empresas, estagios, instituicoes, cursos, cidades, seguradoras, relatorios] = await Promise.all([
        alunoService.list({ page: 1, limit: 1 }),
        empresaService.list({ page: 1, limit: 1 }),
        estagioService.list({ page: 1, limit: 1 }),
        instituicaoService.list({ page: 1, limit: 1 }),
        cursoService.list({ page: 1, limit: 1 }),
        cidadeService.list({ page: 1, limit: 1 }),
        seguradoraService.list({ page: 1, limit: 1 }),
        relatorioEstagioService.list({ page: 1, limit: 1 }),
      ]);

      return {
        totalAlunos: alunos?.pagination?.total || 0,
        totalEmpresas: empresas?.pagination?.total || 0,
        totalEstagios: estagios?.pagination?.total || 0,
        totalInstituicoes: instituicoes?.pagination?.total || 0,
        totalCursos: cursos?.pagination?.total || 0,
        totalCidades: cidades?.pagination?.total || 0,
        totalSeguradoras: seguradoras?.pagination?.total || 0,
        totalRelatorios: relatorios?.pagination?.total || 0,
      };
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
      return {
        totalAlunos: 0,
        totalEmpresas: 0,
        totalEstagios: 0,
        totalInstituicoes: 0,
        totalCursos: 0,
        totalCidades: 0,
        totalSeguradoras: 0,
        totalRelatorios: 0,
      };
    }
  },

  getEstagiosAtivos: async () => {
    try {
      const estagiosAtivos = await estagioService.getByStatus('Ativo');
      return estagiosAtivos;
    } catch (error) {
      console.error('Erro ao buscar estágios ativos:', error);
      return [];
    }
  },

  getEstagiosRecentes: async () => {
    try {
      const response = await estagioService.list({ page: 1, limit: 5 });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar estágios recentes:', error);
      return [];
    }
  },
};

export default api;
